import type { ComponentPropsWithoutRef, ReactElement, ReactNode } from 'react';
// oxlint-disable-next-line no-restricted-imports -- hrefs are site-prefixed by the root loader; project Link calls useSite
import { Link } from 'react-router';
import type { CustomerCareLinkKind } from '@/lib/customer-care-links';
import { useCustomerCareHref } from '@/hooks/use-customer-care-href';

export type CustomerCareLinkProps = Omit<ComponentPropsWithoutRef<'a'>, 'href'> & {
    children?: ReactNode;
    /** Destination within the help center. Defaults to the hub (Assistenza). */
    kind?: CustomerCareLinkKind;
};

/**
 * Link to a help-center destination from the `customerCareLinks` site preference.
 * Must not depend on SiteProvider (Page Designer can stream this in without it), so it uses
 * React Router's Link with the href the root loader already site-prefixed.
 * Renders nothing when the preference has no destination for `kind`; wrap surrounding copy in a
 * `useCustomerCareHref` check so it is hidden too.
 */
export default function CustomerCareLink({
    children,
    kind = 'hub',
    ...linkProps
}: CustomerCareLinkProps): ReactElement | null {
    const href = useCustomerCareHref(kind);
    if (!href) {
        return null;
    }
    return (
        <Link to={href} {...linkProps}>
            {children}
        </Link>
    );
}
